import { useEffect, useRef } from "react";
import { useI18N, useOnboardingResult } from "common/hooks";
import { createPageAnalytics } from "common/analytics";
import Loader from "@/components/common/Loader";
import { useAtom } from "jotai";
import {
  onboardingCompletedAtom,
  onboardingCurrentScreenAtom,
  onboardingScreenAtom,
} from "@/store/atoms";
import { useCustomToast } from "@/hooks/useCustomToast";
import { bucketUrlPrefix } from "common/config";
import Modal from "react-responsive-modal";
import Slider from "react-slick";
import { useRouter } from "@tanstack/react-router";
import { useDarkMode } from "@/hooks/useDarkMode";

const SafeSlider = Slider as any;

function shouldOpenOnboardingModal(
  initialRoute: string | string[],
  queryParams: URLSearchParams,
) {
  const excludedRoutes = ["/passwordreset", "/verifyuser", "/venuedetails"];
  const excludedQueryParams = ["utm_source", "utm_medium", "utm_term"];

  const isRouteExcluded = excludedRoutes.some((route) =>
    initialRoute.includes(route),
  );
  const isQueryExcluded = excludedQueryParams.some((param) =>
    queryParams.has(param),
  );

  return !(isRouteExcluded || isQueryExcluded);
}

interface ModalProps {
  onClose: () => void;
}

function OnboardingScreensComponent(props: ModalProps) {
  const { onClose } = props;
  const i18n = useI18N();
  const showToast = useCustomToast();
  const isDark = useDarkMode();
  const { data, error, isLoading } = useOnboardingResult({
    isDark,
    isMobile: false,
  });
  const [isOnboardingCompleted, setIsOnboardingCompleted] = useAtom(
    onboardingCompletedAtom,
  );
  const sliderRef = useRef<Slider | null>(null);
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] =
    useAtom(onboardingScreenAtom);
  const [onboardingCurrentScreen, setOnboardingCurrentScreen] = useAtom(
    onboardingCurrentScreenAtom,
  );

  const onboardingAnalytics = createPageAnalytics("onboarding");
  // Tracks the slide we're leaving so afterChange can derive navigation_direction.
  const prevScreenRef = useRef(onboardingCurrentScreen);
  // Guards the one-time page_view per modal open (reset when the modal closes).
  const hasTrackedOpenRef = useRef(false);

  const totalSlides = data?.photos?.length || 0;

  const router = useRouter();
  const initialRouteRef = useRef(null);

  useEffect(() => {
    if (data && !isOnboardingCompleted) {
      console.log("open onboarding modal");
      setIsOnboardingModalOpen(true);
      setIsOnboardingCompleted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isOnboardingCompleted]);

  useEffect(() => {
    if (error) {
      showToast(error.message, "error");
    }
  }, [error]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Tab" && !e.shiftKey) {
        sliderRef.current?.slickNext();
      } else if (e.key === "Tab" && e.shiftKey) {
        sliderRef.current?.slickPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [sliderRef]);

  const handleFinishOnboarding = () => {
    console.log("handleFinishOnboarding");

    // Wizard completion — onboarding_completed (stub event, no props).
    onboardingAnalytics.track("lets_start_action_button", {});

    setIsOnboardingCompleted(true);
    setIsOnboardingModalOpen(false);
  };

  const handleSkipOnboarding = () => {
    // Skip — onboarding_skipped, scoped to the slide the user skipped from.
    // Distinct from completion (onboarding_completed): do NOT route through
    // handleFinishOnboarding or the legacy "completed" events.
    onboardingAnalytics.track("skip_action_button", {
      step_index: onboardingCurrentScreen + 1,
      step_name: data?.photos?.[onboardingCurrentScreen]?.title ?? "",
    });

    setIsOnboardingCompleted(true);
    setIsOnboardingModalOpen(false);
  };

  const firstFocusRef = useRef(null);

  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (!initialRouteRef.current) {
      initialRouteRef.current = router.state.location.pathname;
    }
  }, [router.state.location.pathname]);

  const queryParams = new URLSearchParams(
    Object.entries(router.state.location.search).map(([key, value]) => [
      key,
      String(value),
    ]),
  );
  const shouldOpenModal =
    isOnboardingModalOpen &&
    shouldOpenOnboardingModal(initialRouteRef.current, queryParams);

  // Onboarding page-view — fired once per open (the only mount-style tracking call).
  useEffect(() => {
    if (shouldOpenModal && totalSlides > 0) {
      if (!hasTrackedOpenRef.current) {
        hasTrackedOpenRef.current = true;
        prevScreenRef.current = onboardingCurrentScreen;
        onboardingAnalytics.track("page_view_page", {
          navigation_direction: "next",
          previous_step_index: 0,
          step_index: onboardingCurrentScreen + 1,
          step_name: data?.photos?.[onboardingCurrentScreen]?.title ?? "",
        });
      }
    } else {
      hasTrackedOpenRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldOpenModal, totalSlides]);

  const isFirstSlide = onboardingCurrentScreen === 0;
  const isLastSlide =
    totalSlides > 0 && onboardingCurrentScreen === totalSlides - 1;

  return (
    <>
      {isLoading && <Loader />}
      <Modal
        focusTrapped
        open={shouldOpenModal}
        onClose={onClose}
        ariaLabelledby="onboarding-dialog-title"
        classNames={{
          root: "",
          modal: "imagelistviewer-modal",
        }}
        styles={{
          modal: {
            maxWidth: "784px",
            width: "100%",
            maxHeight: "98vh",
          },
        }}
      >
        <div className="w-full flex flex-col bg-bg-brand-secondary-default rounded-2xl overflow-hidden text-fg-neutral-strong md:max-h-[90vh]">

          {/* Accessible dialog name — visually hidden, read by AT on modal open */}
          <span id="onboarding-dialog-title" className="sr-only">
            {i18n.get("beforeYouUseSociability")}
          </span>

          {/* Live region — announces slide position on every navigation */}
          <div
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
          >
            {totalSlides > 0 &&
              `${i18n.get("slide") || "Slide"} ${onboardingCurrentScreen + 1} ${i18n.get("of") || "of"} ${totalSlides}: ${data?.photos?.[onboardingCurrentScreen]?.title ?? ""}`}
          </div>

          {/* Progress bar + Skip — driven by current slide index */}
          <div className="flex items-center gap-3 px-6 pt-6">
            <div
              className="flex flex-1 items-center gap-3"
              aria-hidden="true"
              role="presentation"
            >
              {Array.from({ length: totalSlides }).map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 h-2 rounded-full transition-colors duration-300 ${
                    i <= onboardingCurrentScreen
                      ? "bg-container-brand-subtle"
                      : "bg-container-brand-minimal"
                  }`}
                />
              ))}
            </div>

            {/* Skip — dismisses onboarding, mirroring the native onboarding */}
            {totalSlides > 0 && (
              <button
                type="button"
                onClick={handleSkipOnboarding}
                className="shrink-0 inline-flex items-center min-h-[44px] px-1 label-m-medium text-links focus-blue"
              >
                {i18n.get("skip")}
              </button>
            )}
          </div>

          {/* Slide content */}
          <div
            className="flex-1 min-h-0 px-6 pt-6 overflow-hidden"
            role="region"
            aria-roledescription="carousel"
            aria-label={i18n.get("beforeYouUseSociability")}
          >
            <SafeSlider
              ref={sliderRef}
              dots={false}
              afterChange={(index) => {
                // Per-step landing — single chokepoint for Next, Previous and Tab nav.
                const prev = prevScreenRef.current;
                if (index !== prev) {
                  const direction = index > prev ? "next" : "previous";
                  onboardingAnalytics.track(
                    direction === "next"
                      ? "next_action_button"
                      : "previous_action_button",
                    {
                      navigation_direction: direction,
                      previous_step_index: prev + 1,
                      step_index: index + 1,
                      step_name: data?.photos?.[index]?.title ?? "",
                    },
                  );
                }
                prevScreenRef.current = index;

                setOnboardingCurrentScreen(index);
              }}
              initialSlide={onboardingCurrentScreen}
              nextArrow={null}
              prevArrow={null}
              infinite={false}
              arrows={false}
              accessibility={false}
            >
              {data?.photos?.map((photo, index) => (
                <div
                  key={index}
                  tabIndex={-1}
                  role="group"
                  aria-roledescription="slide"
                  aria-labelledby={`slide-title-${index}`}
                  aria-describedby={`slide-description-${index}`}
                >
                  <div className="flex flex-col gap-3 pb-2">
                    {/* Title */}
                    <h2
                      id={`slide-title-${index}`}
                      className="headline-l-bold text-fg-neutral-strong [letter-spacing:-0.384px]"
                    >
                      {photo.title}
                    </h2>

                    {/* Description */}
                    <p
                      id={`slide-description-${index}`}
                      className="body-l-semibold text-fg-neutral-subtle pb-2"
                    >
                      {photo.description}
                    </p>

                    {/* Screenshot image — decorative; slide text already describes the feature */}
                    <img
                      aria-hidden="true"
                      alt=""
                      src={bucketUrlPrefix + photo.image}
                      className="w-full h-auto object-cover rounded-xl min-h-[150px] md:min-h-[250px] max-h-[45vh] shadow-[0px_1px_2px_0px_rgba(7,7,8,0.08),0px_2px_6px_0px_rgba(7,7,8,0.12)]"
                    />
                  </div>
                </div>
              ))}
            </SafeSlider>
          </div>

          {/* Button group */}
          <div className="flex items-center gap-4 p-4">
            {/* Previous — hidden on first slide */}
            {!isFirstSlide && (
              <button
                type="button"
                tabIndex={0}
                onClick={() => sliderRef.current?.slickPrev()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    sliderRef.current?.slickPrev();
                  }
                }}
                className="flex-1 min-h-[40px] px-2.5 bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-lg flex items-center justify-center focus-blue transition-colors hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active"
              >
                <span className="label-m-medium text-fg-neutral-strong">
                  {i18n.get("previous")}
                </span>
              </button>
            )}

            {/* Next or Finish */}
            {isLastSlide ? (
              <button
                type="button"
                tabIndex={0}
                onClick={handleFinishOnboarding}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleFinishOnboarding();
                  }
                }}
                className="flex-1 min-h-[40px] px-2.5 bg-bg-brand-primary-default rounded-lg flex items-center justify-center focus-blue transition-colors hover:bg-bg-brand-primary-hover active:bg-bg-brand-primary-active"
              >
                <span className="label-m-medium text-fg-neutral-onFilled">
                  {i18n.get("startExploring")}
                </span>
              </button>
            ) : (
              <button
                type="button"
                tabIndex={0}
                onClick={() => sliderRef.current?.slickNext()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    sliderRef.current?.slickNext();
                  }
                }}
                className="flex-1 min-h-[40px] px-2.5 bg-bg-brand-primary-default rounded-lg flex items-center justify-center focus-blue transition-colors hover:bg-bg-brand-primary-hover active:bg-bg-brand-primary-active"
              >
                <span className="label-m-medium text-fg-neutral-onFilled">
                  {i18n.get("next")}
                </span>
              </button>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}

export default OnboardingScreensComponent;
